import axios from 'axios';
import { useEffect, useRef } from 'react';
import { useBalances } from '../../queries';
import { useZeroXDepositCurrencies, useZeroXDepositMinimum } from '../../queries/zero-x';
import { useZeroXAddress } from '../../mutations/useZeroXAddress';
import type { ZeroXAddress, ZeroXDepositMinimumState } from '../../schemas/zeroXDeposit';

type DepositPhase =
  | { name: 'selecting' | 'loading' | 'pending' }
  | { name: 'ready'; destination: Extract<ZeroXAddress, { state: 'ready' }> }
  | { name: 'error'; messageKey: string; canRetry: boolean };

const ADDRESS_ERROR_KEYS: Record<number, string> = {
  400: 'zeroXCurrencyUnsupported',
  409: 'zeroXAddressConflict',
};

const DEFAULT_DEPOSIT_CURRENCY = 'USDT (BEP20)';

const getDepositPhase = (request: ReturnType<typeof useZeroXAddress>): DepositPhase => {
  switch (request.status) {
    case 'idle':
      return { name: 'selecting' };

    case 'pending':
      return { name: 'loading' };

    case 'error': {
      const status = axios.isAxiosError(request.error) ? request.error.response?.status : undefined;
      const messageKey = ADDRESS_ERROR_KEYS[status ?? 0] ?? 'zeroXUnavailable';

      return { name: 'error', messageKey, canRetry: status !== 400 && status !== 409 };
    }

    case 'success':
      if (request.data.state === 'ready') {
        return { name: 'ready', destination: request.data };
      }

      return { name: 'pending' };
  }
};

export const useZeroXDeposit = () => {
  useBalances({ refetchInterval: 10_000 });

  const currencies = useZeroXDepositCurrencies();
  const request = useZeroXAddress();
  const defaultRequested = useRef(false);
  const { mutate } = request;
  const currency = request.variables?.currency ?? '';
  const minimumQuery = useZeroXDepositMinimum(currency);

  let minimum: ZeroXDepositMinimumState = { status: 'idle' };

  if (currency) {
    minimum = { status: 'unavailable' };

    if (minimumQuery.isPending || minimumQuery.isFetching) {
      minimum = { status: 'loading' };
    } else if (!minimumQuery.isError && minimumQuery.data?.currency === currency) {
      const { effectiveMinimum, asOf } = minimumQuery.data;

      if (effectiveMinimum !== null && asOf !== null) {
        minimum = { status: 'available', data: { ...minimumQuery.data, effectiveMinimum, asOf } };
      }
    }
  }

  const defaultAvailable = currencies.data?.currencies.includes(DEFAULT_DEPOSIT_CURRENCY) === true;
  const waitingForDefault = request.status === 'idle' && (currencies.isLoading || defaultAvailable);
  const phase: DepositPhase = waitingForDefault ? { name: 'loading' } : getDepositPhase(request);

  useEffect(() => {
    if (!defaultAvailable || currency || defaultRequested.current) {
      return;
    }

    defaultRequested.current = true;
    mutate({ currency: DEFAULT_DEPOSIT_CURRENCY });
  }, [defaultAvailable, currency, mutate]);

  const selectCurrency = (value: string) => {
    if (request.isPending || value === currency || !currencies.data?.currencies.includes(value)) {
      return;
    }

    request.mutate({ currency: value });
  };

  const retry = () => {
    const canRetry = phase.name === 'pending' || (phase.name === 'error' && phase.canRetry);

    if (canRetry) {
      request.mutate({ currency });
    }
  };

  return {
    currencies,
    currency,
    phase,
    selectCurrency,
    retry,
    minimum,
    retryMinimum: minimumQuery.refetch,
  };
};
